import { useCallback, useEffect, useState } from 'react';
import { Image, Modal, Stack, Text, UnstyledButton } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import type { EmblaCarouselType } from 'embla-carousel';

type MenuItemImageModalProps = {
  opened: boolean;
  onClose: () => void;
  title: string;
  images: string[];
};

export const MenuItemImageModal: React.FC<MenuItemImageModalProps> = ({
  opened,
  onClose,
  title,
  images,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mainEmbla, setMainEmbla] = useState<EmblaCarouselType | null>(null);
  const [thumbsEmbla, setThumbsEmbla] = useState<EmblaCarouselType | null>(null);

  const hasThumbnails = images.length > 1;

  const onThumbClick = useCallback(
    (index: number) => {
      if (!mainEmbla) return;
      mainEmbla.scrollTo(index);
      setSelectedIndex(index);
    },
    [mainEmbla]
  );

  const onSelect = useCallback(() => {
    if (!mainEmbla) return;
    const index = mainEmbla.selectedScrollSnap();
    setSelectedIndex(index);
    thumbsEmbla?.scrollTo(index);
  }, [mainEmbla, thumbsEmbla]);

  useEffect(() => {
    if (!mainEmbla) return;
    onSelect();
    mainEmbla.on('select', onSelect).on('reInit', onSelect);
    return () => {
      mainEmbla.off('select', onSelect).off('reInit', onSelect);
    };
  }, [mainEmbla, onSelect]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      centered
      title={
        <Text fw={600} size="lg">
          {title}
        </Text>
      }
    >
      <Stack gap="sm">
        <Carousel withControls={false} height={382} slideGap={0} getEmblaApi={setMainEmbla}>
          {images.map((image, index) => (
            <Carousel.Slide key={`${image}-${index}`}>
              <Image src={image} alt={title} h={382} w="100%" radius="sm" fit="cover" />
            </Carousel.Slide>
          ))}
        </Carousel>

        {hasThumbnails && (
          <Carousel
            withControls={false}
            height={65}
            slideSize="98px"
            slideGap="sm"
            getEmblaApi={setThumbsEmbla}
            emblaOptions={{ containScroll: 'keepSnaps', dragFree: true }}
          >
            {images.map((image, index) => (
              <Carousel.Slide key={`${image}-${index}`}>
                <UnstyledButton
                  onClick={() => onThumbClick(index)}
                  aria-label={`Show image ${index + 1}`}
                  aria-current={index === selectedIndex}
                  style={{ display: 'block', lineHeight: 0 }}
                >
                  <Image
                    src={image}
                    alt=""
                    w={98}
                    h={65}
                    radius="sm"
                    fit="cover"
                    style={{
                      opacity: index === selectedIndex ? 1 : 0.5,
                      outline:
                        index === selectedIndex
                          ? '3px solid var(--mantine-color-blue-5)'
                          : undefined,
                      outlineOffset: -3,
                    }}
                  />
                </UnstyledButton>
              </Carousel.Slide>
            ))}
          </Carousel>
        )}
      </Stack>
    </Modal>
  );
};

